import type { ActionItemStatus, ObjectiveStatus } from "./api";

export type StatusVariant = "success" | "warning" | "danger" | "info" | "neutral";

export interface StatusMeta {
  key: ObjectiveStatus;
  label: string;
}

export const STATUS_META: StatusMeta[] = [
  { key: "not_started", label: "Not started" },
  { key: "in_progress", label: "In progress" },
  { key: "at_risk", label: "At risk" },
  { key: "on_hold", label: "On hold" },
  { key: "complete", label: "Complete" },
];

export const STATUS_BY_KEY: Record<ObjectiveStatus, StatusMeta> = Object.fromEntries(
  STATUS_META.map((s) => [s.key, s]),
) as Record<ObjectiveStatus, StatusMeta>;

export const STATUS_VARIANT: Record<ObjectiveStatus, StatusVariant> = {
  not_started: "neutral",
  in_progress: "info",
  at_risk: "danger",
  on_hold: "warning",
  complete: "success",
};

export const ACTION_STATUS_META: { key: ActionItemStatus; label: string }[] = [
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "done", label: "Done" },
  { key: "cancelled", label: "Cancelled" },
];

export const ACTION_STATUS_VARIANT: Record<ActionItemStatus, StatusVariant> = {
  open: "neutral",
  in_progress: "info",
  done: "success",
  cancelled: "neutral",
};

export const VARIANT_BG_CLASS: Record<StatusVariant, string> = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
  neutral: "bg-neutral",
};

export const ACTION_SOURCE_LABEL: Record<string, string> = {
  exec_meeting: "Exec meeting",
  one_on_one: "1:1",
  manual: "Manual",
  m365: "M365",
};

// Owner accents are picked from the DS extended palette — professional,
// visually distinct hues. Reach for these tokens (not raw hex) for any
// per-category accent in reports, dashboards, etc.
const OWNER_TOKENS = [
  "hsl(var(--cinch-brand))",       // cyan
  "hsl(var(--status-purple))",     // purple
  "hsl(var(--status-teal))",       // teal
  "hsl(var(--status-orange))",     // orange
  "hsl(var(--cinch-power-deep))",  // deep blue
  "hsl(var(--status-success))",    // green
  "hsl(var(--cinch-brand-deep))",  // deep cyan
];

const UNASSIGNED_COLOR = "hsl(var(--status-neutral))";

export function ownerColor(seed: string | null | undefined): string {
  if (!seed) return UNASSIGNED_COLOR;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return OWNER_TOKENS[Math.abs(hash) % OWNER_TOKENS.length] ?? UNASSIGNED_COLOR;
}

// The API stores dates as "YYYY-MM-DD"; the Cinch <DatePicker> works in Date
// objects. Convert at the boundary, parsing as local midnight (no UTC shift).
export function isoToDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function dateToIso(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
